import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { COLORS, FONTS, SHADOWS, GRADIENTS } from '../styles/tokens';

/** Shown when a user has no app: roles (account pending admin approval) */
export function PendingApprovalView(): React.JSX.Element {
  const { isDark } = useTheme();
  const { logout } = useAuth();
  const colors = isDark ? COLORS.dark : COLORS.light;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '24px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          backgroundColor: colors.card,
          borderRadius: '12px',
          boxShadow: isDark ? SHADOWS.neumorphicDark : SHADOWS.neumorphicLight,
          padding: '48px 32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          transition: 'background-color 0.3s',
        }}
      >
        {/* Hourglass icon */}
        <div
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            backgroundColor: isDark ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.08)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>

        {/* Heading */}
        <h2
          style={{
            fontFamily: FONTS.heading,
            fontSize: '14px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: colors.text,
            margin: 0,
            textAlign: 'center',
          }}
        >
          ACCOUNT PENDING APPROVAL
        </h2>

        {/* Description */}
        <p
          style={{
            fontFamily: FONTS.body,
            fontSize: '13px',
            lineHeight: '1.6',
            color: colors.textSecondary,
            margin: 0,
            textAlign: 'center',
          }}
        >
          Your account is being reviewed by an administrator. You will be granted access to applications once your request has been approved.
        </p>

        {/* Sign Out button */}
        <button
          onClick={logout}
          style={{
            width: '100%',
            height: '44px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            background: isDark ? GRADIENTS.buttonDark : GRADIENTS.buttonLight,
            color: '#FFFFFF',
            fontFamily: FONTS.heading,
            fontSize: '12px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}
        >
          SIGN OUT
        </button>

        {/*
          Dev-only bypass. `import.meta.env.DEV` is a compile-time constant
          (false in production builds), so this entire block is stripped from
          staging/prod bundles by Vite's dead-code elimination.
        */}
        {import.meta.env.DEV && (
          <button
            onClick={() => {
              localStorage.setItem('vertex-dev-bypass-approval', '1');
              window.location.reload();
            }}
            style={{
              width: '100%',
              height: '36px',
              borderRadius: '8px',
              border: `1px dashed ${colors.textSecondary}`,
              cursor: 'pointer',
              background: 'transparent',
              color: colors.textSecondary,
              fontFamily: FONTS.heading,
              fontSize: '10px',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
            }}
            title="Local dev only — not present in staging/prod builds"
          >
            DEV · SKIP APPROVAL
          </button>
        )}
      </div>
    </div>
  );
}
